import { Controller } from 'stimulus'

export default class extends Controller {
  static targets = ['searchInput', 'clearSearchButton']

  connect() {
    this.moduleCollapsed = null
    this._currentSearch = null
    this._lazyLoadedHandler = this._onLazyContentLoaded.bind(this)
    document.addEventListener('lazy-content:loaded', this._lazyLoadedHandler)
    this._lessonIndex = this._parseLessonIndex()
  }

  disconnect() {
    document.removeEventListener('lazy-content:loaded', this._lazyLoadedHandler)
  }

  _parseLessonIndex() {
    const el = this.element.querySelector('[data-course-lesson-search-index]')
    if (!el) return null
    try {
      return JSON.parse(el.textContent)
    } catch (e) {
      console.warn('Failed to parse lesson search index:', e)
      return null
    }
  }

  _onLazyContentLoaded(event) {
    if (!this._currentSearch) return
    if (!this.element.contains(event.target)) return
    // A module's lessons just loaded while search is active — re-apply filter to new DOM content
    this.revealSearchResults(this._currentSearch)
  }

  search() {
    const termLower = this.searchInputTarget.value
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .replace(/^\s+|\s+$/g, '')
    if (termLower === '') {
      this.endSearch()
    } else {
      this.clearSearchButtonTarget.classList.add('opacity-100')
      this.revealSearchResults(termLower)
    }
  }

  clearSearch() {
    this.searchInputTarget.value = ''
    this.endSearch()
  }

  _match(value, termLower) {
    return value.replace(/\s+/g, ' ').includes(termLower)
  }

  _moduleMatches(moduleEl, termLower) {
    const searchValue = moduleEl.querySelector(
      '[data-course-module-search-value]'
    )?.dataset.courseModuleSearchValue
    return !!searchValue && this._match(searchValue, termLower)
  }

  rememberModulesState() {
    if (this.moduleCollapsed) return
    this.moduleCollapsed = {}
    this.element.querySelectorAll('.js-course-module').forEach((elm) => {
      this.moduleCollapsed[elm.id] = elm
        .querySelector('.js-content')
        .classList.contains('hidden')
    })
  }

  restoreModulesState() {
    this.element.querySelectorAll('.js-course-module').forEach((elm) => {
      elm.classList.remove('hidden')
      const isCollapsed = this.moduleCollapsed[elm.id]
      elm.querySelector('.js-content').classList.toggle('hidden', isCollapsed)
      elm
        .querySelector('.js-chevron')
        .classList.toggle('fa-rotate-90', !isCollapsed)
      elm
        .querySelector('.js-course-module-description')
        ?.classList.remove('hidden')
    })
    this.moduleCollapsed = null
  }

  _matchingModuleDomIds(termLower) {
    if (!this._lessonIndex) return null
    const ids = new Set()
    this._lessonIndex.forEach((lesson) => {
      if (this._match(lesson.searchValue, termLower))
        ids.add(lesson.moduleDomId)
    })
    return ids
  }

  revealSearchResults(termLower) {
    this._currentSearch = termLower
    this.rememberModulesState()

    const matchingModuleIds = this._matchingModuleDomIds(termLower)

    if (matchingModuleIds) {
      // Match module titles as well as pre-loaded lesson metadata before loading lessons
      this.element.querySelectorAll('.js-course-module').forEach((moduleEl) => {
        const moduleMatches = this._moduleMatches(moduleEl, termLower)
        if (moduleMatches || matchingModuleIds.has(moduleEl.id)) {
          moduleEl.classList.remove('hidden')
          moduleEl.querySelector('.js-content').classList.remove('hidden')
          moduleEl.querySelector('.js-chevron').classList.add('fa-rotate-90')
          moduleEl
            .querySelector('.js-course-module-description')
            ?.classList.toggle('hidden', !moduleMatches)

          const lazyEl = moduleEl.querySelector(
            '[data-controller~="lazy-content"]'
          )
          if (lazyEl) {
            // Lessons not yet in DOM — trigger lazy load so user sees them
            const controller =
              this.application.getControllerForElementAndIdentifier(
                lazyEl,
                'lazy-content'
              )
            controller?.loadContent()
          } else {
            // Lessons already in DOM — filter them individually
            moduleEl
              .querySelectorAll('.js-course-lesson')
              .forEach((lessonEl) => {
                lessonEl.classList.toggle(
                  'hidden',
                  !moduleMatches &&
                    !this._match(lessonEl.dataset.searchValue, termLower)
                )
              })
          }
        } else {
          moduleEl.classList.add('hidden')
          moduleEl.querySelector('.js-content').classList.add('hidden')
        }
      })
    } else {
      // No index (flat lesson list, no lazy loading) — DOM-only filtering
      this.element.querySelectorAll('.js-course-lesson').forEach((elm) => {
        elm.classList.toggle(
          'hidden',
          !this._match(elm.dataset.searchValue, termLower)
        )
      })
      this.element.querySelectorAll('.js-course-module').forEach((elm) => {
        const moduleMatches = this._moduleMatches(elm, termLower)
        if (moduleMatches) {
          elm.querySelectorAll('.js-course-lesson').forEach((lessonEl) => {
            lessonEl.classList.remove('hidden')
          })
        }
        const content = elm.querySelector('.js-content')
        const hasMatch =
          moduleMatches ||
          elm.querySelectorAll('.js-course-lesson:not(.hidden)').length > 0
        if (hasMatch) {
          elm.querySelector('.js-chevron').classList.add('fa-rotate-90')
          content.classList.remove('hidden')
          elm.classList.remove('hidden')
          elm
            .querySelector('.js-course-module-description')
            ?.classList.toggle('hidden', !moduleMatches)
        } else {
          elm.classList.add('hidden')
          content.classList.add('hidden')
        }
      })
    }
  }

  endSearch() {
    this._currentSearch = null
    if (this.moduleCollapsed) this.restoreModulesState()
    this.element
      .querySelectorAll('.js-course-lesson')
      .forEach((elm) => elm.classList.remove('hidden'))
    this.clearSearchButtonTarget.classList.remove('opacity-100')
  }
}
